import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaCode, FaMobile, FaServer, FaRobot, FaBrain,
  FaShoppingCart, FaCloudDownloadAlt, FaUserShield, FaCheck,
} from 'react-icons/fa';
import { HiArrowRight, HiSparkles } from 'react-icons/hi';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import useHeroEntrance from '../hooks/useHeroEntrance';
import { prefersReducedMotion } from '../hooks/useReducedMotion';

gsap.registerPlugin(ScrollTrigger);

const Services = () => {
  const [activeTab, setActiveTab] = useState('all');
  const location = useLocation();
  const navigate = useNavigate();
  const heroBadgeRef = useRef(null);
  const heroH1Ref = useRef(null);
  const heroParaRef = useRef(null);
  useHeroEntrance([
    heroBadgeRef,
    { ref: heroH1Ref, heading: true },
    heroParaRef,
  ]);

  useEffect(() => {
    const conditionData = localStorage.getItem('condition');
    if (conditionData) {
      setActiveTab(conditionData);
    } else {
      const path = location.pathname.split('/').pop();
      if (path && path !== 'services') {
        setActiveTab(path.replace(/-/g, ''));
      } else {
        setActiveTab('all');
      }
    }
  }, [location]);

  const services = [
    {
      id: 'web', icon: <FaCode />, title: 'Web Development',
      description: 'Custom, responsive websites and web applications built with the latest technologies.',
      features: ['Custom website development', 'Progressive Web Apps (PWA)', 'Single Page Applications (SPA)', 'E-commerce development', 'Content Management Systems', 'Performance optimization'],
      technologies: ['React', 'Angular', 'Vue.js', 'Node.js', 'PHP', 'WordPress', 'Laravel'],
      color: 'from-nexcess-blue to-nexcess-cyan',
    },
    {
      id: 'mobile', icon: <FaMobile />, title: 'Mobile App Development',
      description: 'Native and cross-platform mobile applications for iOS and Android devices.',
      features: ['iOS app development', 'Android app development', 'Cross-platform solutions', 'App performance optimization', 'Maintenance and support'],
      technologies: ['React Native', 'Flutter', 'Swift', 'Kotlin', 'Firebase', 'AWS Amplify'],
      color: 'from-nexcess-cyan to-nexcess-purple',
    },
    {
      id: 'cloud', icon: <FaServer />, title: 'Cloud Services',
      description: 'Scalable cloud solutions for your business needs, from migration to optimization.',
      features: ['Cloud architecture design', 'Cloud migration services', 'DevOps implementation', 'Serverless solutions', 'Infrastructure as Code', 'Cloud security'],
      technologies: ['AWS', 'Google Cloud', 'Azure', 'Docker', 'Kubernetes', 'Terraform'],
      color: 'from-nexcess-purple to-nexcess-blue',
    },
    {
      id: 'ai', icon: <FaRobot />, title: 'AI Integration',
      description: 'Leverage the power of artificial intelligence and machine learning in your applications.',
      features: ['AI strategy & consulting', 'Machine learning models', 'Natural language processing', 'Computer vision', 'Predictive analytics', 'Chatbots & virtual assistants'],
      technologies: ['TensorFlow', 'N8N', 'OpenAI', 'Google AI', 'Azure AI', 'IBM Watson'],
      color: 'from-nexcess-blue to-nexcess-purple',
    },
    {
      id: 'ecommerce', icon: <FaShoppingCart />, title: 'E-Commerce Solutions',
      description: 'End-to-end e-commerce solutions to help your business sell online effectively.',
      features: ['Custom e-commerce development', 'Payment gateway integration', 'Shopping cart customization', 'Product management systems', 'Order fulfillment systems', 'Multi-channel selling'],
      technologies: ['Shopify', 'WooCommerce', 'Magento', 'BigCommerce', 'Stripe', 'PayPal'],
      color: 'from-nexcess-cyan to-nexcess-blue',
    },
    {
      id: 'blockchain', icon: <FaBrain />, title: 'Blockchain Development',
      description: 'Custom blockchain solutions for secure, transparent, and decentralized applications.',
      features: ['Smart contract development', 'DApp development', 'Tokenization', 'NFT marketplace development', 'Private blockchain networks', 'Blockchain consulting'],
      technologies: ['Ethereum', 'Solidity', 'Web3.js', 'Hyperledger', 'Binance Smart Chain', 'IPFS'],
      color: 'from-nexcess-purple to-nexcess-cyan',
    },
    {
      id: 'devops', icon: <FaCloudDownloadAlt />, title: 'DevOps Services',
      description: 'Streamline your development and operations with our DevOps expertise.',
      features: ['CI/CD pipeline setup', 'Infrastructure automation', 'Containerization', 'Microservices architecture', 'Monitoring & logging', 'Performance optimization'],
      technologies: ['Jenkins', 'GitHub Actions', 'GitLab CI', 'Docker', 'Kubernetes', 'Prometheus'],
      color: 'from-nexcess-blue to-nexcess-cyan',
    },
    {
      id: 'security', icon: <FaUserShield />, title: 'Cybersecurity',
      description: 'Comprehensive security solutions to protect your digital assets and data.',
      features: ['Security assessments', 'Penetration testing', 'Vulnerability management', 'Security architecture design', 'Compliance consulting', 'Incident response planning'],
      technologies: ['OWASP', 'Burp Suite', 'Nessus', 'Metasploit', 'Wireshark', 'Cloudflare'],
      color: 'from-nexcess-cyan to-nexcess-purple',
    },
  ];

  const categories = [
    { id: 'all', label: 'All' },
    { id: 'web', label: 'Web' },
    { id: 'mobile', label: 'Mobile' },
    { id: 'cloud', label: 'Cloud' },
    { id: 'ai', label: 'AI' },
    { id: 'ecommerce', label: 'E-Commerce' },
    { id: 'blockchain', label: 'Blockchain' },
    { id: 'devops', label: 'DevOps' },
    { id: 'security', label: 'Security' },
  ];

  const filteredServices = activeTab === 'all' ? services : services.filter((s) => s.id === activeTab);

  const outerRef = useRef(null);
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const headingRef = useRef(null);

  useLayoutEffect(() => {
    if (prefersReducedMotion()) return;
    let ctx;
    // Delay so DOM is painted and scrollWidth is accurate
    const timer = setTimeout(() => {
      const outer = outerRef.current;
      const track = trackRef.current;
      if (!outer || !track) return;

      // How far the track needs to slide = track width minus viewport width
      const totalScroll = track.scrollWidth - window.innerWidth;
      if (totalScroll <= 0) return;

      // Stretch the outer wrapper so the scroll distance matches the slide distance
      outer.style.height = `calc(100vh + ${totalScroll}px)`;

      // Seed heading at light weight
      const heading = headingRef.current;
      if (heading) heading.style.fontVariationSettings = '"wght" 300';

      ctx = gsap.context(() => {
        gsap.to(track, {
          x: -totalScroll,
          ease: 'none',
          scrollTrigger: {
            trigger: outer,
            start: 'top top',
            end: () => `+=${totalScroll}`,
            scrub: 1,
            invalidateOnRefresh: true,
            onRefresh: () => {
              const newTotal = track.scrollWidth - window.innerWidth;
              outer.style.height = `calc(100vh + ${newTotal}px)`;
            },
            onUpdate: (self) => {
              if (heading) {
                const wght = Math.round(300 + self.progress * 500);
                heading.style.fontVariationSettings = `"wght" ${wght}`;
              }
            },
          },
        });
      });
    }, 150);

    return () => {
      clearTimeout(timer);
      if (ctx) ctx.revert();
    };
  }, []);

  useEffect(() => {
    if (prefersReducedMotion()) return;
    let handlers = [];
    const t = setTimeout(() => {
      // Target the inner tilt div — keeps GSAP completely separate from Framer Motion's element
      const wrappers = document.querySelectorAll('.service-card');
      handlers = Array.from(wrappers).map((wrapper) => {
        const inner = wrapper.querySelector('.tilt-inner');
        if (!inner) return null;

        const onMove = (e) => {
          const rect = wrapper.getBoundingClientRect();
          const cx = rect.left + rect.width / 2;
          const cy = rect.top + rect.height / 2;
          const rotY = ((e.clientX - cx) / (rect.width / 2)) * 15;
          const rotX = -((e.clientY - cy) / (rect.height / 2)) * 15;
          gsap.to(inner, {
            rotationY: rotY,
            rotationX: rotX,
            transformPerspective: 900,
            transformOrigin: 'center center',
            ease: 'power3.out',
            duration: 0.35,
            overwrite: 'auto',
          });
        };

        const onLeave = () => {
          gsap.to(inner, {
            rotationX: 0,
            rotationY: 0,
            scale: 1,
            ease: 'elastic.out(1, 0.4)',
            duration: 0.9,
            overwrite: 'auto',
          });
        };

        const onDown = () => gsap.to(inner, { scale: 0.97, duration: 0.12, ease: 'power2.out', overwrite: 'auto' });
        const onUp   = () => gsap.to(inner, { scale: 1,    duration: 0.25, ease: 'power2.out', overwrite: 'auto' });

        wrapper.addEventListener('mousemove',  onMove);
        wrapper.addEventListener('mouseleave', onLeave);
        wrapper.addEventListener('mousedown',  onDown);
        wrapper.addEventListener('mouseup',    onUp);
        return { wrapper, onMove, onLeave, onDown, onUp };
      }).filter(Boolean);
    }, 450);

    return () => {
      clearTimeout(t);
      handlers.forEach(({ wrapper, onMove, onLeave, onDown, onUp }) => {
        wrapper.removeEventListener('mousemove',  onMove);
        wrapper.removeEventListener('mouseleave', onLeave);
        wrapper.removeEventListener('mousedown',  onDown);
        wrapper.removeEventListener('mouseup',    onUp);
      });
    };
  }, [activeTab]);

  /* Process cards: smooth entrance stagger when section enters viewport */
  useEffect(() => {
    if (prefersReducedMotion()) return;
    const ctx = gsap.context(() => {
      const cards = gsap.utils.toArray('.process-card');
      if (!cards.length) return;
      gsap.set(cards, { opacity: 0, y: 30, scale: 0.96, willChange: 'transform' });
      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: 'top 70%',
        once: true,
        onEnter: () => {
          gsap.to(cards, {
            opacity: 1, y: 0, scale: 1,
            duration: 0.85, stagger: 0.1, ease: 'power3.out',
          });
        },
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  const process = [
    { number: '01', title: 'Discovery & Requirements', description: 'We begin by understanding your business goals, challenges, and requirements through in-depth consultations and research.' },
    { number: '02', title: 'Planning & Strategy', description: 'Based on the gathered requirements, we develop a comprehensive strategy and detailed project plan with timelines and milestones.' },
    { number: '03', title: 'Design & Prototyping', description: 'Our design team creates intuitive and visually appealing interfaces, and we develop prototypes for your review and feedback.' },
    { number: '04', title: 'Development & Implementation', description: 'Our skilled development team brings the designs to life using the latest technologies and best coding practices.' },
    { number: '05', title: 'Testing & Quality Assurance', description: 'We conduct rigorous testing to ensure that the solution is bug-free, secure, and meets all the specified requirements.' },
    { number: '06', title: 'Deployment & Launch', description: 'Once approved, we deploy the solution to the production environment and ensure a smooth launch.' },
    { number: '07', title: 'Support & Maintenance', description: 'We provide ongoing support and maintenance to ensure the continued success and optimal performance of your solution.' },
  ];

  return (
    <>
      {/* HERO */}
      <section className="relative pt-36 pb-16 overflow-hidden bg-paper">
        <div className="absolute inset-0 bg-mesh-soft pointer-events-none" />
        <div className="absolute top-20 right-0 w-[500px] h-[500px] rounded-full bg-nexcess-blue/8 blur-3xl animate-blob pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-nexcess-purple/8 blur-3xl animate-blob pointer-events-none" style={{ animationDelay: '4s' }} />

        <div className="container-custom relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <div ref={heroBadgeRef} className="badge mb-5 mx-auto">
              <HiSparkles /> Our Capabilities
            </div>
            <h1 ref={heroH1Ref} className="display-hero mb-5">
              Our <span className="gradient-text">Services</span>
            </h1>
            <p ref={heroParaRef} className="narrative-lg">
              Comprehensive digital solutions tailored to transform your business and drive
              growth in the digital landscape.
            </p>
          </div>
        </div>
      </section>

      {/* TABS */}
      <section className="pb-6 bg-paper">
        <div className="container-custom">
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-2">
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveTab(c.id)}
                className={`shrink-0 px-5 py-2.5 text-sm font-semibold rounded-full border transition-all duration-300 ${
                  activeTab === c.id
                    ? 'text-white border-transparent shadow-soft'
                    : 'bg-white text-ink-soft border-hairline hover:text-nexcess-blue hover:border-nexcess-blue/40'
                }`}
                style={
                  activeTab === c.id
                    ? { background: 'linear-gradient(135deg, #0984e3 0%, #00b0ff 60%, #d500f9 100%)' }
                    : {}
                }
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="py-12 bg-paper">
        <div className="container-custom">
          <AnimatePresence mode="popLayout">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredServices.map((service, index) => (
                <motion.article
                  key={service.id}
                  layout
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ delay: index * 0.06, duration: 0.4 }}
                  className="service-card"
                  style={{ perspective: '900px' }}
                >
                  {/* GSAP only ever touches this inner div — Framer Motion never sees it */}
                  <div
                    className="tilt-inner group bg-white rounded-2xl p-7 border border-hairline shadow-soft hover:shadow-soft-lg flex flex-col h-full"
                    style={{ transformStyle: 'preserve-3d', willChange: 'transform' }}
                  >
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center text-white text-2xl mb-5 shadow-soft group-hover:scale-110 transition-transform duration-300`}>
                      {service.icon}
                    </div>

                    <h3 className="heading-sm mb-3">{service.title}</h3>
                    <p className="narrative-body mb-5 text-sm">{service.description}</p>

                    <div className="text-xs uppercase tracking-wider text-nexcess-blue font-semibold mb-3">Features</div>
                    <ul className="space-y-2 mb-6">
                      {service.features.map((f, i) => (
                        <li key={i} className="text-sm text-ink-soft flex items-start gap-2">
                          <FaCheck className="text-nexcess-blue mt-1 shrink-0" size={11} />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="text-xs uppercase tracking-wider text-nexcess-blue font-semibold mb-3">Tech Stack</div>
                    <div className="flex flex-wrap gap-1.5 mb-6">
                      {service.technologies.map((t) => (
                        <span
                          key={t}
                          className="text-xs px-2.5 py-1 rounded-md bg-paper-2 text-ink-soft border border-hairline font-medium"
                        >
                          {t}
                        </span>
                      ))}
                    </div>

                    <button
                      onClick={() => navigate('/contact')}
                      className="mt-auto inline-flex items-center gap-1 text-sm font-semibold text-nexcess-blue group-hover:gap-2 transition-all"
                    >
                      Discuss Project <HiArrowRight />
                    </button>
                  </div>
                </motion.article>
              ))}
            </div>
          </AnimatePresence>
        </div>
      </section>

      {/* PROCESS — outer wrapper creates the scroll distance; inner section is CSS-sticky */}
      <div ref={outerRef} className="bg-paper-2">
        <section
          ref={sectionRef}
          className="sticky top-0 h-screen overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="container-custom pt-16 pb-8 shrink-0">
            <div className="max-w-2xl mx-auto text-center">
              <div data-reveal="up" data-reveal-delay="0.05" className="badge mb-4">Our Process</div>
              <h2 ref={headingRef} className="heading-xl mb-5" style={{ fontVariationSettings: '"wght" 300' }}>
                How We <span className="gradient-text">Deliver</span> Excellence
              </h2>
              <p className="narrative-lg">
                Our systematic approach ensures that we deliver high-quality solutions that meet your business needs.
              </p>
            </div>
          </div>

          {/* Horizontal track */}
          <div className="flex-1 flex items-center overflow-hidden">
            <div
              ref={trackRef}
              className="flex gap-6 will-change-transform"
              style={{ paddingLeft: '5vw', paddingRight: '5vw' }}
            >
              {process.map((step, i) => (
                <div
                  key={step.number}
                  className="process-card relative bg-white rounded-2xl p-8 border border-hairline shadow-soft hover:shadow-soft-lg hover:-translate-y-1 transition-all duration-300 flex-shrink-0"
                  style={{ width: '320px' }}
                >
                  <div className="text-6xl font-extrabold gradient-text leading-none mb-5 select-none">
                    {step.number}
                  </div>
                  <h3 className="font-bold text-ink mb-3 text-lg leading-snug">{step.title}</h3>
                  <p className="text-sm text-ink-soft leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Scroll hint */}
          <div className="shrink-0 pb-6 flex justify-center">
            <div className="flex items-center gap-2 text-xs text-ink-soft opacity-60 select-none">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M2 8h12M10 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Scroll to explore
            </div>
          </div>
        </section>
      </div>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-paper">
        <div className="container-custom">
          <div className="relative overflow-hidden rounded-3xl p-10 md:p-16 text-center text-white shadow-soft-lg" style={{ background: 'linear-gradient(135deg, #0984e3 0%, #00b0ff 50%, #d500f9 100%)' }}>
            <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
            <div className="relative z-10 max-w-2xl mx-auto">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 text-xs uppercase tracking-wider font-semibold mb-6">
                <HiSparkles /> Next Step
              </div>
              <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-5 text-white">
                Ready to Transform Your Business?
              </h2>
              <p className="text-white/90 text-lg mb-8 max-w-xl mx-auto">
                Let's discuss how our services can help you achieve your business goals.
              </p>
              <button
                onClick={() => navigate('/contact')}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-white text-nexcess-blue font-semibold shadow-soft hover:-translate-y-0.5 hover:shadow-2xl transition-all duration-300"
              >
                Schedule a Consultation <HiArrowRight />
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Services;
